<template>
    <transition name="fade">
        <div class="modal is-active" v-show="visible">
            <div class="modal-background" @click="destroy"></div>
            <div class="modal-content">
                <g-form
                    class="popup-dialog"
                    :form="form"
                    :action="`/api/admin/transaction/${this.id}/refund`"
                    @success="handleSuccess"
                    @errors="handleErrors"
                >
                    <div class="popup-title">
                        <h1 class="title is-1">{{ $t('admin.transactions.reverse.title') }}</h1>

                        <div class="popup-close" @click.prevent="destroy">
                            <i class="icon">close</i>
                        </div>
                    </div>

                    <div class="popup-body">
                        <!-- Information section -->
                        <div class="columns">
                            <div class="column is-one-quarter">
                                <strong>{{ $t('admin.transactions.reverse.id') }}</strong>
                            </div>
                            <div class="column">{{ id }}</div>
                        </div>
                        <div class="columns">
                            <div class="column is-one-quarter">
                                <strong>{{ $t('admin.transactions.reverse.original_amount') }}</strong>
                            </div>
                            <div class="column">{{ originalAmount / 100 | currency }}</div>
                        </div>
                        <div class="columns">
                            <div class="column is-one-quarter">
                                <strong>{{ $t('admin.transactions.reverse.type') }}</strong>
                            </div>
                            <div class="column">{{ $t('transactions.status.' + transactionData.type) }}</div>
                        </div>
                        <div class="columns">
                            <div class="column is-one-quarter">
                                <strong>{{ $t('admin.transactions.reverse.date') }}</strong>
                            </div>
                            <div class="column">{{ $i18nDate(new Date(transactionData.captured_at), 'long') }}</div>
                        </div>
                        <div class="columns">
                            <div class="column is-one-quarter">
                                <strong>{{ $t('admin.transactions.reverse.description') }}</strong>
                            </div>
                            <div class="column">{{ transactionData.description }}</div>
                        </div>

                        <hr class="divider">

                        <!-- Refund Information Section -->
                        <div class="columns" v-if="refundOptions.already_refunded_amount > 0">
                            <div class="column is-one-third">
                                <strong>{{ $t('admin.transactions.reverse.already_refunded') }}</strong>
                            </div>
                            <div class="column has-text-warning">
                                {{ refundOptions.already_refunded_amount / 100 | currency }}
                            </div>
                        </div>

                        <div class="columns">
                            <div class="column is-one-third">
                                <strong>{{ $t('admin.transactions.reverse.max_refundable') }}</strong>
                            </div>
                            <div class="column">{{ refundOptions.max_refund_amount / 100 | currency }}</div>
                        </div>

                        <hr class="divider">

                        <!-- Refund Form Fields -->
                        <div v-if="loading === false">
                            <g-format
                                :label="$t('admin.transactions.reverse.refund_amount')"
                                name="amount"
                                type="currency"
                                ref="amount"
                            />

                            <g-select
                                name="reason"
                                :label="$t('admin.transactions.reverse.reason')"
                                :options="refundOptions.reasons"
                            />

                            <g-input
                                :label="$t('admin.transactions.reverse.reason_description')"
                                name="reason_description"
                                type="textarea"
                                :rows="3"
                                :maxlength="500"
                                :placeholder="$t('admin.transactions.reverse.reason_description_placeholder')"
                            />
                        </div>

                        <!-- Summary Section -->
                        <div class="notification is-info is-light" v-if="form.amount">
                            <p>
                                <strong>{{ $t('admin.transactions.reverse.summary') }}</strong><br/>
                                {{
                                    $t('admin.transactions.reverse.summary_text', {
                                        amount: this.$options.filters.currency(parseFloat(form.amount.toString().replace(',', '.')))
                                    })
                                }}
                            </p>
                        </div>
                    </div>

                    <div class="popup-footer">
                        <a @click.prevent="destroy" href="#" role="button">{{
                                $t('admin.transactions.reverse.cancel')
                            }}</a>
                        <g-button :loading="loading" :disabled="!canSubmit">
                            {{ $t('admin.transactions.reverse.reverse') }}
                        </g-button>
                    </div>
                </g-form>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: "TransactionReverseForm",
    data() {
        return {
            form: new Form({
                amount: 0,
                reason: '',
                reason_description: '',
            }),
            transactionData: {
                amount: 0,
                type: null,
                captured_at: null,
                description: null,
            },
            refundOptions: {
                reasons: {},
                max_refund_amount: 0,
                original_amount: 0,
                already_refunded_amount: 0,
            },
            loading: true,
        }
    },
    props: {
        id: {
            type: String,
        },
    },
    computed: {
        originalAmount() {
            return Math.abs(this.refundOptions.original_amount);
        },
        canSubmit() {
            const amountInCents = this.form.amount ? parseFloat(this.form.amount.toString().replace(',', '.')) * 100 : 0;

            return this.form.reason &&
                (amountInCents >= 1 && amountInCents <= this.refundOptions.max_refund_amount);
        }
    },
    mounted() {
        this.loadTransactionData();
    },
    methods: {
        async loadTransactionData() {
            try {
                const response = await axios.get(`/api/admin/transaction/${this.id}/refund`);

                if (response.data && response.data.data) {
                    const {transaction, refund_options} = response.data.data;

                    // Set transaction data
                    this.transactionData = {
                        amount: transaction.amount,
                        type: transaction.type,
                        captured_at: transaction.captured_at,
                        description: transaction.description,
                    };

                    // Add a placeholder to the reason select options
                    refund_options.reasons.unshift({
                        name: this.$t('admin.transactions.reverse.select_reason'),
                        value: '',
                    });

                    // Set refund options
                    this.refundOptions = refund_options;

                    // Default is to refund the full amount
                    this.form.amount = this.refundOptions.max_refund_amount / 100;

                    this.loading = false;
                }
            } catch (error) {
                if (error.response && error.response.status === 422) {
                    this.$buefy.toast.open({
                        message: error.response.data.message || this.$t('admin.transactions.reverse.not_reversible'),
                        type: 'is-danger'
                    });
                } else {
                    this.$buefy.toast.open({
                        message: this.$t('admin.transactions.reverse.load_error'),
                        type: 'is-danger'
                    });
                }
                this.destroy();
            }
        },
        handleSuccess(response) {
            this.$buefy.toast.open({
                message: this.$t('admin.transactions.reverse.success', {amount: this.$options.filters.currency((this.form.amount || this.refundOptions.max_refund_amount) / 100)}),
                type: 'is-success'
            });

            this.destroy(response.data);
        },
        handleErrors(errors) {
            if (errors.response && errors.response.data && errors.response.data.message) {
                this.$buefy.toast.open({message: errors.response.data.message, type: 'is-danger'});
            } else {
                this.$buefy.toast.open({message: this.$t('admin.transactions.reverse.error'), type: 'is-danger'});
            }
        }
    }
}
</script>

<style scoped>
.divider {
    margin: 1.5rem 0;
}

.notification {
    margin-top: 1.5rem;
}
</style>
